import axios from 'axios';

import i18n from '../locals/i18n';
import { snackbarActions } from '../redux';
import store from '../redux/createStore';

let apiRootUrl = process.env.REACT_APP_API_HOST;

class Network {
  static setHost(rootUrl) {
    apiRootUrl = rootUrl;
  }

  static getUrl(route) {
    if (route.indexOf('http://') === 0 || route.indexOf('https://') === 0 || route.indexOf('www.') === 0) {
      return route;
    }
    return `${apiRootUrl}${route}`;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  static getUrlWithParams(route: string, params: Record<string, any>): string {
    let url = Network.getUrl(route);
    if (params) {
      for (const property in params) {
        if (params.hasOwnProperty(property)) {
          url = Network.addQueryStringParameter(url, property, params[property]);
        }
      }
    }
    return url;
  }

  static addQueryStringParameter(uri, key, value) {
    const re = new RegExp(`([?&])${key}=.*?(&|$)`, 'i');
    const separator = uri.indexOf('?') !== -1 ? '&' : '?';
    if (uri.match(re)) {
      return uri.replace(re, `$1${key}=${value}$2`);
    }
    return `${uri + separator + key}=${value}`;
  }

  static basicHeaders(): Record<string, string> {
    const headers: Record<string, string> = {};

    headers['Content-Type'] = 'application/json';
    headers['Content-Security-Policy'] = "default-src 'self'";
    headers.Accept = 'application/json';

    return headers;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  static errorHandler(error: { message?: string; response?: any; request?: any }) {
    if (error?.message === 'Network Error') {
      store.dispatch(snackbarActions.showMessage('ERROR', i18n.t('errorFailedToContactServerTitle')));
    }
    if (error.response) {
      throw {
        errors: (error.response.data && error.response.data.errors) || [
          {
            code: '0',
            status: 500,
            title: 'Unknown error',
            meta: error.response,
          },
        ],
      };
    } else if (error.request) {
      // The request was made but no response was received
      throw {
        errors: [
          {
            code: '0',
            status: 500,
            title: 'Unknown error',
            meta: error.request,
          },
        ],
      };
    } else {
      // Something happened in setting up the request that triggered an Error
      throw {
        errors: [
          {
            code: '0',
            status: 500,
            title: 'Unknown error',
            meta: error.message,
          },
        ],
      };
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  static async get(route, params = {}, headers = this.basicHeaders()): Promise<any> {
    try {
      const result = await axios.get(this.getUrlWithParams(route, params), { headers, withCredentials: true });
      return result.data;
    } catch (err) {
      this.errorHandler(err);
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  static async put(route, body = {}, headers = this.basicHeaders()): Promise<any> {
    try {
      const result = await axios.put(this.getUrl(route), body, { headers, withCredentials: true });
      return result.data;
    } catch (err) {
      this.errorHandler(err);
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  static async patch(route, body = {}, headers = this.basicHeaders()): Promise<any> {
    try {
      const result = await axios.patch(this.getUrl(route), body, { headers, withCredentials: true });
      return result.data;
    } catch (err) {
      this.errorHandler(err);
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  static async post(route, body = {}, headers = this.basicHeaders()): Promise<any> {
    try {
      const result = await axios.post(this.getUrl(route), body, { headers, withCredentials: true });
      return result.data;
    } catch (err) {
      this.errorHandler(err);
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  static async delete(route, headers = this.basicHeaders()): Promise<any> {
    try {
      const result = await axios.delete(this.getUrl(route), { headers, withCredentials: true });
      return result.data || true;
    } catch (err) {
      this.errorHandler(err);
    }
  }
}

export default Network;
